import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import Card from '../../components/common/Card';
import Button from '../../components/common/Button';
import Modal from '../../components/common/Modal';
import { timeEntryService, userService } from '../../services/api';
import { useTranslation } from 'react-i18next';
import { formatHoursMinutes, formatMinutes } from '../../utils/formatHours';

// Customers read worked time as H:MM. Serialized entries already ship a
// formatted duration; the decimal `hours` field is the fallback.
const entryDuration = (entry) => entry?.durationFormatted || formatHoursMinutes(entry?.hours);

// Monthly aggregates arrive in both dialects. Prefer the exact integer minutes:
// `hours` is stored as DECIMAL(5,2), so three 7h20m days sum to 21.99 and render
// 21:59 while the rows below add up to 22:00. The decimal stays as the fallback
// for a response cached before the *Minutes fields shipped.
//   aggregateDuration(<INTEGER MINUTES>, <DECIMAL HOURS>)
const aggregateDuration = (minutes, hours) =>
  minutes === null || minutes === undefined || minutes === ''
    ? formatHoursMinutes(Number(hours) || 0)
    : formatMinutes(minutes);

// Styled components
const FilterContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing[4]};
  margin-bottom: ${({ theme }) => theme.spacing[4]};
`;

const FilterGroup = styled.div`
  display: flex;
  flex-direction: column;
`;

const FilterLabel = styled.label`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.dark};
  margin-bottom: ${({ theme }) => theme.spacing[2]};
`;

const FilterSelect = styled.select`
  padding: ${({ theme }) => theme.spacing[2]} ${({ theme }) => theme.spacing[3]};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borders.radius.md};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  min-width: 140px;
  background-color: ${({ theme }) => theme.colors.white};

  @media (max-width: 768px) {
    font-size: 16px; /* >=16px prevents iOS focus zoom */
    min-height: 44px;
  }
`;

const TableContainer = styled.div`
  overflow-x: auto;
  margin-top: ${({ theme }) => theme.spacing[4]};
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const TableHead = styled.thead`
  background-color: ${({ theme }) => theme.colors.light};
`;

const TableHeadCell = styled.th`
  padding: ${({ theme }) => theme.spacing[3]};
  text-align: left;
  font-weight: ${({ theme }) => theme.typography.fontWeight.semiBold};
  color: ${({ theme }) => theme.colors.dark};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  white-space: nowrap;
`;

const TableRow = styled.tr`
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};

  &:hover {
    background-color: ${({ theme }) => theme.colors.lightAlt};
  }
`;

const TableCell = styled.td`
  padding: ${({ theme }) => theme.spacing[3]};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.dark};
`;

const ExpandedRow = styled.tr`
  background-color: ${({ theme }) => theme.colors.lightAlt};
`;

const ExpandedCell = styled.td`
  padding: ${({ theme }) => theme.spacing[4]};
`;

const ExpandedTitle = styled.h4`
  margin: 0 0 ${({ theme }) => theme.spacing[3]};
  font-size: ${({ theme }) => theme.typography.fontSize.md};
  color: ${({ theme }) => theme.colors.dark};
`;

// Status badge
const Badge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.25rem 0.5rem;
  font-size: 0.75rem;
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  border-radius: 9999px;

  ${({ status, theme }) => {
    if (status === 'approved') {
      return `
        background-color: var(--status-approved-plate);
        color: ${theme.colors.success};
      `;
    } else if (status === 'pending') {
      return `
        background-color: var(--status-warning-plate);
        color: ${theme.colors.warning};
      `;
    } else if (status === 'challenged') {
      return `
        background-color: rgba(195,37,29,.25);
        color: ${theme.colors.error};
      `;
    }
    return '';
  }}
`;

const ActionButton = styled.button`
  background: none;
  border: none;
  color: ${({ theme, danger }) => (danger ? theme.colors.error : theme.colors.primary)};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  cursor: pointer;
  padding: 0;
  font-size: ${({ theme }) => theme.typography.fontSize.sm};

  &:hover {
    text-decoration: underline;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    text-decoration: none;
  }
`;

/* Employee list: table on desktop, stacked cards on phones (CSS-only toggle) */
const DesktopTableContainer = styled(TableContainer)`
  @media (max-width: 576px) {
    display: none;
  }
`;

const MobileCards = styled.div`
  display: none;

  @media (max-width: 576px) {
    display: flex;
    flex-direction: column;
    gap: ${({ theme }) => theme.spacing[3]};
    margin-top: ${({ theme }) => theme.spacing[4]};
  }
`;

const MobileCard = styled.div`
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borders.radius.md};
  padding: ${({ theme }) => theme.spacing[3]};
  background-color: ${({ theme }) => theme.colors.white};
`;

const MobileCardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[2]};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semiBold};
  color: ${({ theme }) => theme.colors.dark};
`;

const MobileCardMeta = styled.div`
  margin-top: ${({ theme }) => theme.spacing[1]};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.medium};
`;

const MobileCardActions = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing[2]};
  margin-top: ${({ theme }) => theme.spacing[3]};

  ${ActionButton} {
    flex: 1;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-height: 44px;
    padding: ${({ theme }) => theme.spacing[2]} ${({ theme }) => theme.spacing[3]};
    border: 1px solid ${({ theme }) => theme.colors.border};
    border-radius: ${({ theme }) => theme.borders.radius.md};
  }
`;

const MobileEntries = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[2]};
  margin-top: ${({ theme }) => theme.spacing[3]};
  padding-top: ${({ theme }) => theme.spacing[3]};
  border-top: 1px solid ${({ theme }) => theme.colors.border};
`;

const MobileEntryCard = styled.div`
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borders.radius.md};
  padding: ${({ theme }) => theme.spacing[3]};
  background-color: ${({ theme }) => theme.colors.lightAlt};
`;

const MobileEntryDescription = styled.div`
  margin-top: ${({ theme }) => theme.spacing[1]};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.dark};
`;

const AlertBox = styled.div`
  padding: ${({ theme }) => theme.spacing[3]} ${({ theme }) => theme.spacing[4]};
  margin-bottom: ${({ theme }) => theme.spacing[4]};
  border-radius: ${({ theme }) => theme.borders.radius.md};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  background-color: ${({ variant }) => (variant === 'error' ? '#F8D7DA' : '#D4EDDA')};
  color: ${({ theme, variant }) =>
    variant === 'error' ? theme.colors.error : theme.colors.success};
`;

const ModalActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: ${({ theme }) => theme.spacing[3]};
  margin-top: ${({ theme }) => theme.spacing[4]};

  @media (max-width: 576px) {
    /* Bottom-sheet modal: stacked full-width buttons, primary on top */
    flex-direction: column-reverse;
    gap: ${({ theme }) => theme.spacing[2]};

    & > * {
      width: 100%;
    }
  }
`;

const ReasonTextarea = styled.textarea`
  width: 100%;
  padding: ${({ theme }) => theme.spacing[3]};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borders.radius.md};
  font-size: ${({ theme }) => theme.typography.fontSize.base};
  min-height: 100px;
  resize: vertical;
  margin-top: ${({ theme }) => theme.spacing[2]};

  &:focus {
    border-color: ${({ theme }) => theme.colors.primary};
    outline: none;
    box-shadow: var(--focus-act);
  }
`;

const ModalError = styled.div`
  color: ${({ theme }) => theme.colors.error};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  margin-top: ${({ theme }) => theme.spacing[2]};
`;

const pad2 = (n) => String(n).padStart(2, '0');

const monthDateRange = (year, month) => {
  const lastDay = new Date(year, month, 0).getDate();
  return {
    startDate: `${year}-${pad2(month)}-01`,
    endDate: `${year}-${pad2(month)}-${pad2(lastDay)}`
  };
};

const employeeDisplayName = (employee) =>
  employee.name || `${employee.firstName || ''} ${employee.lastName || ''}`.trim();

const Employees = () => {
  const { t, i18n } = useTranslation();
  const now = new Date();
  const monthName = (m) => new Date(2000, m - 1, 1).toLocaleDateString(i18n.language, { month: 'long' });
  const statusLabel = (status) => t(`status.${status || 'pending'}`);
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Expanded employee (daily entries)
  const [expandedId, setExpandedId] = useState(null);
  const [entries, setEntries] = useState([]);
  const [entriesLoading, setEntriesLoading] = useState(false);
  const [entriesError, setEntriesError] = useState(null);

  // Modals
  const [approveMonthModal, setApproveMonthModal] = useState({ open: false, employee: null });
  const [entryModal, setEntryModal] = useState({ open: false, entry: null, action: '' });
  const [challengeReason, setChallengeReason] = useState('');
  const [modalError, setModalError] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const fetchEmployees = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await userService.getCustomerEmployees({ year, month, limit: 1000, page: 1 });
      setEmployees(data?.items || []);
    } catch (err) {
      setError(t('customer.employees.loadError'));
      setEmployees([]);
    } finally {
      setLoading(false);
    }
  }, [year, month]);

  useEffect(() => {
    fetchEmployees();
  }, [fetchEmployees]);

  const fetchEntries = useCallback(async (employeeId) => {
    try {
      setEntriesLoading(true);
      setEntriesError(null);
      const { startDate, endDate } = monthDateRange(year, month);
      const data = await timeEntryService.getCustomerTimeEntries({
        employeeId,
        startDate,
        endDate,
        limit: 31
      });
      setEntries(data?.items || []);
    } catch (err) {
      setEntriesError(t('customer.employees.entriesLoadError'));
      setEntries([]);
    } finally {
      setEntriesLoading(false);
    }
  }, [year, month]);

  // Collapse the expanded row when the selected month changes
  useEffect(() => {
    setExpandedId(null);
    setEntries([]);
    setEntriesError(null);
  }, [year, month]);

  const toggleExpand = (employee) => {
    if (expandedId === employee.id) {
      setExpandedId(null);
      setEntries([]);
      setEntriesError(null);
      return;
    }
    setExpandedId(employee.id);
    setEntries([]);
    fetchEntries(employee.id);
  };

  const refreshAfterAction = async () => {
    await fetchEmployees();
    if (expandedId) {
      await fetchEntries(expandedId);
    }
  };

  // Approve month
  const openApproveMonthModal = (employee) => {
    setApproveMonthModal({ open: true, employee });
    setModalError(null);
    setSuccess(null);
  };

  const closeApproveMonthModal = () => {
    setApproveMonthModal({ open: false, employee: null });
    setModalError(null);
  };

  const handleApproveMonth = async () => {
    const { employee } = approveMonthModal;
    if (!employee) return;

    try {
      setSubmitting(true);
      setModalError(null);
      const result = await timeEntryService.approveMonth(employee.id, year, month);
      closeApproveMonthModal();
      const count = result?.count ?? 0;
      setSuccess(t('customer.employees.approveMonthSuccess', {
        count,
        name: employeeDisplayName(employee),
        month: monthName(month),
        year
      }));
      await refreshAfterAction();
    } catch (err) {
      setModalError(t('customer.employees.approveMonthError'));
    } finally {
      setSubmitting(false);
    }
  };

  // Per-entry approve / challenge
  const openEntryModal = (entry, action) => {
    setEntryModal({ open: true, entry, action });
    setChallengeReason('');
    setModalError(null);
    setSuccess(null);
  };

  const closeEntryModal = () => {
    setEntryModal({ open: false, entry: null, action: '' });
    setChallengeReason('');
    setModalError(null);
  };

  const handleEntryAction = async () => {
    const { entry, action } = entryModal;
    if (!entry) return;

    if (action === 'challenge' && !challengeReason.trim()) {
      setModalError(t('customer.employees.challengeReasonRequired'));
      return;
    }

    try {
      setSubmitting(true);
      setModalError(null);

      if (action === 'approve') {
        const result = await timeEntryService.approveTimeEntry(entry.id);
        setSuccess(result?.message || t('customer.employees.entryApproved'));
      } else {
        const result = await timeEntryService.challengeTimeEntry(entry.id, challengeReason.trim());
        setSuccess(result?.message || t('customer.employees.entryChallenged'));
      }

      closeEntryModal();
      await refreshAfterAction();
    } catch (err) {
      setModalError(t(action === 'approve' ? 'customer.employees.approveError' : 'customer.employees.challengeError'));
    } finally {
      setSubmitting(false);
    }
  };

  const yearOptions = [];
  for (let y = now.getFullYear(); y >= now.getFullYear() - 3; y--) {
    yearOptions.push(y);
  }

  return (
    <>
      <Card
        title={t('customer.employees.title')}
        subtitle={t('customer.employees.subtitle')}
        padding="md"
      >
        {error && <AlertBox variant="error">{error}</AlertBox>}
        {success && <AlertBox variant="success">{success}</AlertBox>}

        {/* Month / year selector */}
        <FilterContainer>
          <FilterGroup>
            <FilterLabel htmlFor="month">{t('customer.employees.month')}</FilterLabel>
            <FilterSelect
              id="month"
              value={month}
              onChange={(e) => setMonth(Number(e.target.value))}
            >
              {Array.from({ length: 12 }, (_, index) => (
                <option key={index} value={index + 1}>{monthName(index + 1)}</option>
              ))}
            </FilterSelect>
          </FilterGroup>

          <FilterGroup>
            <FilterLabel htmlFor="year">{t('customer.employees.year')}</FilterLabel>
            <FilterSelect
              id="year"
              value={year}
              onChange={(e) => setYear(Number(e.target.value))}
            >
              {yearOptions.map((y) => (
                <option key={y} value={y}>{y}</option>
              ))}
            </FilterSelect>
          </FilterGroup>
        </FilterContainer>

        {/* Employees table */}
        {loading ? (
          <div>{t('customer.employees.loading')}</div>
        ) : (
          <>
          <DesktopTableContainer>
            <Table>
              <TableHead>
                <tr>
                  <TableHeadCell>{t('customer.employees.name')}</TableHeadCell>
                  <TableHeadCell>{t('customer.employees.position')}</TableHeadCell>
                  <TableHeadCell>{t('customer.employees.totalHours')}</TableHeadCell>
                  <TableHeadCell>{t('status.approved')}</TableHeadCell>
                  <TableHeadCell>{t('status.pending')}</TableHeadCell>
                  <TableHeadCell>{t('status.challenged')}</TableHeadCell>
                  <TableHeadCell>{t('common.actions')}</TableHeadCell>
                </tr>
              </TableHead>
              <tbody>
                {employees.length > 0 ? (
                  employees.map((employee) => {
                    const hours = employee.monthlyHours || {};
                    const pendingHours = Number(hours.pendingHours) || 0;
                    const isExpanded = expandedId === employee.id;

                    return (
                      <React.Fragment key={employee.id}>
                        <TableRow>
                          <TableCell>{employeeDisplayName(employee)}</TableCell>
                          <TableCell>{employee.position || '-'}</TableCell>
                          <TableCell>{aggregateDuration(hours.totalMinutes, hours.totalHours)}</TableCell>
                          <TableCell>{aggregateDuration(hours.approvedMinutes, hours.approvedHours)}</TableCell>
                          <TableCell>{aggregateDuration(hours.pendingMinutes, pendingHours)}</TableCell>
                          <TableCell>{aggregateDuration(hours.challengedMinutes, hours.challengedHours)}</TableCell>
                          <TableCell>
                            <div style={{ display: 'flex', gap: '0.75rem' }}>
                              <ActionButton onClick={() => toggleExpand(employee)}>
                                {isExpanded ? t('customer.employees.hideEntries') : t('customer.employees.viewEntries')}
                              </ActionButton>
                              <ActionButton
                                onClick={() => openApproveMonthModal(employee)}
                                disabled={pendingHours === 0}
                                title={pendingHours === 0 ? t('customer.employees.noPendingTitle') : undefined}
                              >
                                {t('customer.employees.approveMonth')}
                              </ActionButton>
                            </div>
                          </TableCell>
                        </TableRow>

                        {isExpanded && (
                          <ExpandedRow>
                            <ExpandedCell colSpan={7}>
                              <ExpandedTitle>
                                {t('customer.employees.entriesTitle', { name: employeeDisplayName(employee), month: monthName(month), year })}
                              </ExpandedTitle>

                              {entriesLoading ? (
                                <div>{t('customer.employees.loadingEntries')}</div>
                              ) : entriesError ? (
                                <AlertBox variant="error">{entriesError}</AlertBox>
                              ) : entries.length > 0 ? (
                                <TableContainer style={{ marginTop: 0 }}>
                                  <Table>
                                    <TableHead>
                                      <tr>
                                        <TableHeadCell>{t('common.date')}</TableHeadCell>
                                        <TableHeadCell>{t('common.project')}</TableHeadCell>
                                        <TableHeadCell>{t('common.hours')}</TableHeadCell>
                                        <TableHeadCell>{t('common.description')}</TableHeadCell>
                                        <TableHeadCell>{t('common.status')}</TableHeadCell>
                                        <TableHeadCell>{t('common.actions')}</TableHeadCell>
                                      </tr>
                                    </TableHead>
                                    <tbody>
                                      {entries.map((entry) => {
                                        const status = entry.status || 'pending';
                                        return (
                                          <TableRow key={entry.id}>
                                            <TableCell>{entry.date}</TableCell>
                                            <TableCell>{entry.projectName || entry.project?.name || '-'}</TableCell>
                                            <TableCell>{entryDuration(entry)}</TableCell>
                                            <TableCell>{entry.description || '-'}</TableCell>
                                            <TableCell>
                                              <Badge status={status}>{statusLabel(status)}</Badge>
                                            </TableCell>
                                            <TableCell>
                                              {status === 'pending' ? (
                                                <div style={{ display: 'flex', gap: '0.75rem' }}>
                                                  <ActionButton onClick={() => openEntryModal(entry, 'approve')}>
                                                    {t('customer.employees.approve')}
                                                  </ActionButton>
                                                  <ActionButton danger onClick={() => openEntryModal(entry, 'challenge')}>
                                                    {t('customer.employees.challenge')}
                                                  </ActionButton>
                                                </div>
                                              ) : (
                                                '-'
                                              )}
                                            </TableCell>
                                          </TableRow>
                                        );
                                      })}
                                    </tbody>
                                  </Table>
                                </TableContainer>
                              ) : (
                                <div>{t('customer.employees.noEntriesMonth')}</div>
                              )}
                            </ExpandedCell>
                          </ExpandedRow>
                        )}
                      </React.Fragment>
                    );
                  })
                ) : (
                  <TableRow>
                    <TableCell colSpan={7} style={{ textAlign: 'center' }}>
                      {t('customer.employees.noEmployees')}
                    </TableCell>
                  </TableRow>
                )}
              </tbody>
            </Table>
          </DesktopTableContainer>

          {/* Stacked cards for phones */}
          <MobileCards>
            {employees.length > 0 ? (
              employees.map((employee) => {
                const hours = employee.monthlyHours || {};
                const pendingHours = Number(hours.pendingHours) || 0;
                const isExpanded = expandedId === employee.id;

                return (
                  <MobileCard key={employee.id}>
                    <MobileCardHeader>
                      <span>{employeeDisplayName(employee)}</span>
                      <span>{t('common.hours')}: {aggregateDuration(hours.totalMinutes, hours.totalHours)}</span>
                    </MobileCardHeader>
                    <MobileCardMeta>
                      {t('status.approved')}: {aggregateDuration(hours.approvedMinutes, hours.approvedHours)}
                      {' · '}{t('status.pending')}: {aggregateDuration(hours.pendingMinutes, pendingHours)}
                      {' · '}{t('status.challenged')}: {aggregateDuration(hours.challengedMinutes, hours.challengedHours)}
                    </MobileCardMeta>
                    <MobileCardActions>
                      <ActionButton onClick={() => toggleExpand(employee)}>
                        {isExpanded ? t('customer.employees.hideEntries') : t('customer.employees.viewEntries')}
                      </ActionButton>
                      <ActionButton
                        onClick={() => openApproveMonthModal(employee)}
                        disabled={pendingHours === 0}
                        title={pendingHours === 0 ? t('customer.employees.noPendingTitle') : undefined}
                      >
                        {t('customer.employees.approveMonth')}
                      </ActionButton>
                    </MobileCardActions>

                    {isExpanded && (
                      <MobileEntries>
                        {entriesLoading ? (
                          <div>{t('customer.employees.loadingEntries')}</div>
                        ) : entriesError ? (
                          <AlertBox variant="error">{entriesError}</AlertBox>
                        ) : entries.length > 0 ? (
                          entries.map((entry) => {
                            const status = entry.status || 'pending';
                            return (
                              <MobileEntryCard key={entry.id}>
                                <MobileCardHeader>
                                  <span>{entry.date}</span>
                                  <Badge status={status}>{statusLabel(status)}</Badge>
                                </MobileCardHeader>
                                <MobileCardMeta>
                                  {entry.projectName || entry.project?.name || '-'}
                                  {' · '}{t('common.hours')}: {entryDuration(entry)}
                                </MobileCardMeta>
                                {entry.description && (
                                  <MobileEntryDescription>{entry.description}</MobileEntryDescription>
                                )}
                                {status === 'pending' && (
                                  <MobileCardActions>
                                    <ActionButton onClick={() => openEntryModal(entry, 'approve')}>
                                      {t('customer.employees.approve')}
                                    </ActionButton>
                                    <ActionButton danger onClick={() => openEntryModal(entry, 'challenge')}>
                                      {t('customer.employees.challenge')}
                                    </ActionButton>
                                  </MobileCardActions>
                                )}
                              </MobileEntryCard>
                            );
                          })
                        ) : (
                          <div>{t('customer.employees.noEntriesMonth')}</div>
                        )}
                      </MobileEntries>
                    )}
                  </MobileCard>
                );
              })
            ) : (
              <div style={{ textAlign: 'center' }}>{t('customer.employees.noEmployees')}</div>
            )}
          </MobileCards>
          </>
        )}
      </Card>

      {/* Approve month confirmation modal */}
      {approveMonthModal.open && (
        <Modal title={t('customer.employees.approveMonthTitle')} onClose={closeApproveMonthModal} size="sm">
          <p>
            {t('customer.employees.approveMonthConfirmBefore')}{' '}
            <strong>{approveMonthModal.employee ? employeeDisplayName(approveMonthModal.employee) : ''}</strong>{' '}
            {t('customer.employees.approveMonthConfirmAfter', { month: monthName(month), year })}
          </p>
          {modalError && <ModalError>{modalError}</ModalError>}
          <ModalActions>
            <Button variant="outline" onClick={closeApproveMonthModal} disabled={submitting}>
              {t('common.cancel')}
            </Button>
            <Button variant="primary" onClick={handleApproveMonth} disabled={submitting}>
              {submitting ? t('customer.employees.approving') : t('customer.employees.approveMonth')}
            </Button>
          </ModalActions>
        </Modal>
      )}

      {/* Entry approve / challenge modal */}
      {entryModal.open && (
        <Modal
          title={entryModal.action === 'approve' ? t('customer.employees.approveEntryTitle') : t('customer.employees.challengeEntryTitle')}
          onClose={closeEntryModal}
          size="sm"
        >
          <p>
            {/* Dedicated H:MM variants of the confirm sentences: the original keys
                spell out "{{hours}} hours", which reads wrong as "7:30 hours". */}
            {entryModal.action === 'approve'
              ? t('customer.employees.approveEntryConfirmHm', { defaultValue: 'Are you sure you want to approve this time entry ({{hours}} on {{date}})?', hours: entryDuration(entryModal.entry), date: entryModal.entry?.date })
              : t('customer.employees.challengeEntryConfirmHm', { defaultValue: 'Are you sure you want to challenge this time entry ({{hours}} on {{date}})?', hours: entryDuration(entryModal.entry), date: entryModal.entry?.date })}
          </p>
          {entryModal.action === 'challenge' && (
            <ReasonTextarea
              value={challengeReason}
              onChange={(e) => setChallengeReason(e.target.value)}
              placeholder={t('customer.employees.challengeReasonPlaceholder')}
            />
          )}
          {modalError && <ModalError>{modalError}</ModalError>}
          <ModalActions>
            <Button variant="outline" onClick={closeEntryModal} disabled={submitting}>
              {t('common.cancel')}
            </Button>
            <Button
              variant={entryModal.action === 'approve' ? 'primary' : 'accent'}
              onClick={handleEntryAction}
              disabled={submitting}
            >
              {submitting ? t('customer.employees.working') : entryModal.action === 'approve' ? t('customer.employees.approve') : t('customer.employees.challenge')}
            </Button>
          </ModalActions>
        </Modal>
      )}
    </>
  );
};

export default Employees;
